import { create } from 'zustand';

import { userApi, type ConsentPatchBody } from '../services/userApi';
import { useAuthStore } from './authStore';

/**
 * Consent state store backed by localStorage.
 *
 * Mirrors the 6-flag model from spec §10.7. `strictlyNecessary` is
 * implicit-true (the platform cannot function without it) and is NOT
 * persisted in this store — it lives only as a derived value when we
 * patch the server. The 5 *toggleable* flags all default to **false**
 * so the platform behaves in a default-deny posture until the user
 * actively chooses.
 *
 * The `hasDecided` flag flips to `true` the first time the user takes
 * ANY action (accept all, reject non-essential, customize-save). The
 * consent banner observes this flag and stops rendering as soon as it
 * goes true — we never re-prompt within a session.
 *
 * When the user is signed in (per `useAuthStore.isAuthenticated`),
 * every mutation also calls `userApi.patchPrivacy()` to persist the
 * choice server-side. Network failures do NOT block the local update:
 * we surface a one-time warning via `lastSyncError` and the UI shows a
 * toast. The store's `lastChangedAt` is always the canonical local
 * timestamp; `ConsentManagedSync` uses it to resolve server-vs-local
 * conflicts (server wins iff its `consentedAt` is newer).
 *
 * Anonymous-session reconciliation (cookie-backed consent for users
 * who never sign in) is deferred to a Phase 5 follow-up.
 */

const STORAGE_KEY = 'roundrecon.consent';

export type ConsentFlag =
  | 'analytics'
  | 'personalization'
  | 'behavioralAdvertising'
  | 'advertiserDataSharing'
  | 'marketingEmail';

export interface ConsentFlags {
  analytics: boolean;
  personalization: boolean;
  behavioralAdvertising: boolean;
  advertiserDataSharing: boolean;
  marketingEmail: boolean;
}

export interface ConsentSnapshot extends ConsentFlags {
  hasDecided: boolean;
  lastChangedAt: string | null;
}

export interface ConsentState extends ConsentSnapshot {
  lastSyncError: string | null;
  acceptAll: () => void;
  rejectAll: () => void;
  acceptOnly: (flags: Partial<ConsentFlags>) => void;
  setFlag: (flag: ConsentFlag, value: boolean) => void;
  hydrateFromStorage: () => void;
  applyServerSnapshot: (snapshot: ConsentSnapshot) => void;
  clearSyncError: () => void;
}

const DEFAULT_FLAGS: ConsentFlags = {
  analytics: false,
  personalization: false,
  behavioralAdvertising: false,
  advertiserDataSharing: false,
  marketingEmail: false,
};

const DEFAULT_SNAPSHOT: ConsentSnapshot = {
  ...DEFAULT_FLAGS,
  hasDecided: false,
  lastChangedAt: null,
};

function readSnapshot(): ConsentSnapshot {
  if (typeof window === 'undefined') return { ...DEFAULT_SNAPSHOT };
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return { ...DEFAULT_SNAPSHOT };
    const parsed = JSON.parse(raw) as Partial<ConsentSnapshot>;
    return {
      analytics: Boolean(parsed.analytics),
      personalization: Boolean(parsed.personalization),
      behavioralAdvertising: Boolean(parsed.behavioralAdvertising),
      advertiserDataSharing: Boolean(parsed.advertiserDataSharing),
      marketingEmail: Boolean(parsed.marketingEmail),
      hasDecided: Boolean(parsed.hasDecided),
      lastChangedAt: typeof parsed.lastChangedAt === 'string' ? parsed.lastChangedAt : null,
    };
  } catch {
    return { ...DEFAULT_SNAPSHOT };
  }
}

function writeSnapshot(snapshot: ConsentSnapshot): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(snapshot));
  } catch {
    /* ignore (private mode, quota) */
  }
}

function snapshotFromState(state: ConsentState): ConsentSnapshot {
  return {
    analytics: state.analytics,
    personalization: state.personalization,
    behavioralAdvertising: state.behavioralAdvertising,
    advertiserDataSharing: state.advertiserDataSharing,
    marketingEmail: state.marketingEmail,
    hasDecided: state.hasDecided,
    lastChangedAt: state.lastChangedAt,
  };
}

function patchBodyFromFlags(flags: ConsentFlags): ConsentPatchBody {
  return {
    analytics: flags.analytics,
    personalization: flags.personalization,
    behavioralAdvertising: flags.behavioralAdvertising,
    advertiserDataSharing: flags.advertiserDataSharing,
    marketingEmail: flags.marketingEmail,
  };
}

async function syncToServer(flags: ConsentFlags): Promise<string | null> {
  const isAuthed = useAuthStore.getState().isAuthenticated;
  if (!isAuthed) return null;
  try {
    await userApi.patchPrivacy(patchBodyFromFlags(flags));
    return null;
  } catch (err) {
    const message = err instanceof Error ? err.message : 'Unknown sync error';
    console.warn('[consent] failed to sync consent to server:', message);
    return message;
  }
}

function persistAndSync(snapshot: ConsentSnapshot, set: (partial: Partial<ConsentState>) => void) {
  writeSnapshot(snapshot);
  void syncToServer(snapshot).then((err) => {
    if (err) set({ lastSyncError: err });
  });
}

export const useConsentStore = create<ConsentState>((set, get) => ({
  ...readSnapshot(),
  lastSyncError: null,

  acceptAll() {
    const next: ConsentSnapshot = {
      analytics: true,
      personalization: true,
      behavioralAdvertising: true,
      advertiserDataSharing: true,
      marketingEmail: true,
      hasDecided: true,
      lastChangedAt: new Date().toISOString(),
    };
    set({ ...next, lastSyncError: null });
    persistAndSync(next, set);
  },

  rejectAll() {
    const next: ConsentSnapshot = {
      ...DEFAULT_FLAGS,
      hasDecided: true,
      lastChangedAt: new Date().toISOString(),
    };
    set({ ...next, lastSyncError: null });
    persistAndSync(next, set);
  },

  acceptOnly(flags) {
    const next: ConsentSnapshot = {
      analytics: Boolean(flags.analytics),
      personalization: Boolean(flags.personalization),
      behavioralAdvertising: Boolean(flags.behavioralAdvertising),
      advertiserDataSharing: Boolean(flags.advertiserDataSharing),
      marketingEmail: Boolean(flags.marketingEmail),
      hasDecided: true,
      lastChangedAt: new Date().toISOString(),
    };
    set({ ...next, lastSyncError: null });
    persistAndSync(next, set);
  },

  setFlag(flag, value) {
    const current = get();
    const next: ConsentSnapshot = {
      ...snapshotFromState(current),
      [flag]: value,
      hasDecided: true,
      lastChangedAt: new Date().toISOString(),
    };
    set({ ...next, lastSyncError: null });
    persistAndSync(next, set);
  },

  hydrateFromStorage() {
    const snapshot = readSnapshot();
    set({ ...snapshot });
  },

  applyServerSnapshot(snapshot) {
    set({ ...snapshot, lastSyncError: null });
    writeSnapshot(snapshot);
  },

  clearSyncError() {
    set({ lastSyncError: null });
  },
}));

/** Storage key exposed for tests / fixtures. */
export const CONSENT_STORAGE_KEY = STORAGE_KEY;
